/* global Office */

/**
 * utils/office.js
 * Fonctions utilitaires pour interagir avec l'API Office.js de manière défensive.
 */

/**
 * Retourne le mode d'affichage courant :
 *  - "compose" : l'utilisateur rédige un brouillon / nouvel email / réponse ;
 *  - "read"    : l'utilisateur consulte un email existant.
 * En composition, subject/destinataires sont des objets exposant getAsync ;
 * en lecture, ce sont des valeurs simples. Par défaut "read" hors d'Outlook.
 */
export function getDisplayMode() {
  const item = window.Office?.context?.mailbox?.item;
  if (!item) return "read";
  return typeof item.subject?.getAsync === "function" ? "compose" : "read";
}

/** Retourne vrai si l'utilisateur rédige un brouillon. */
export function isComposeMode() {
  return getDisplayMode() === "compose";
}

/** Retourne vrai si le panel est ouvert en mode lecture (email existant). */
export function isReadMode() {
  return getDisplayMode() === "read";
}

/**
 * Retourne le corps de l'email (lecture) ou du brouillon (composition) en
 * texte brut (async). body.getAsync est disponible dans les deux modes.
 */
export function getEmailBody() {
  return new Promise((resolve, reject) => {
    if (!window.Office?.context?.mailbox?.item?.body) {
      return resolve("Ceci est un faux texte pour tester hors d'Outlook.");
    }

    Office.context.mailbox.item.body.getAsync(
      Office.CoercionType.Text,
      (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) resolve(res.value);
        else reject(res.error);
      }
    );
  });
}

/**
 * Retourne le corps de l'email en texte brut, débarrassé de la signature et du
 * bloc de citation/réponse (mêmes délimiteurs qu'en composition). Utilisé pour
 * la traduction afin de ne traduire que le message principal.
 */
export function getEmailBodyWithoutSignature() {
  return new Promise((resolve) => {
    const item = window.Office?.context?.mailbox?.item;
    if (!item?.body?.getAsync) {
      return resolve("Ceci est un faux texte pour tester hors d'Outlook.");
    }

    item.body.getAsync(Office.CoercionType.Html, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) {
        resolve(stripSignatureToText(res.value || ""));
        return;
      }
      // Repli : texte brut complet si la version HTML est indisponible.
      item.body.getAsync(Office.CoercionType.Text, (txtRes) => {
        resolve(txtRes.status === Office.AsyncResultStatus.Succeeded ? txtRes.value || "" : "");
      });
    });
  });
}

/**
 * Retourne le sujet de l'email/brouillon (async, compatible lecture + composition).
 * En composition, le sujet doit être lu via getAsync.
 */
export function getEmailSubject() {
  return new Promise((resolve) => {
    const item = window.Office?.context?.mailbox?.item;
    if (!item) return resolve("Sujet de test");

    if (typeof item.subject?.getAsync === "function") {
      item.subject.getAsync((res) => {
        resolve(res.status === Office.AsyncResultStatus.Succeeded ? res.value || "" : "");
      });
    } else {
      resolve(item.subject || "");
    }
  });
}

/**
 * Retourne le premier destinataire du brouillon (nom ou email), ou "" (async).
 * Utilisé pour afficher un contexte pertinent en mode composition.
 */
export function getDraftRecipient() {
  return new Promise((resolve) => {
    const item = window.Office?.context?.mailbox?.item;
    if (!item || typeof item.to?.getAsync !== "function") return resolve("");

    item.to.getAsync((res) => {
      if (res.status !== Office.AsyncResultStatus.Succeeded || !res.value?.length) {
        return resolve("");
      }
      const r = res.value[0];
      resolve(r.displayName || r.emailAddress || "");
    });
  });
}

/** Retourne l'email de l'utilisateur Outlook connecté. */
export function getUserEmail() {
  return window.Office?.context?.mailbox?.userProfile?.emailAddress || "test@local.dev";
}

/**
 * Sélecteurs délimitant la partie à conserver dans un brouillon
 */
const KEEP_BOUNDARY_SELECTORS = [
  "#divRplyFwdMsg", // Outlook bureau : bloc de réponse/transfert cité
  "#appendonsend", // Nouvel Outlook / OWA : marqueur de citation
  "blockquote", // citation (clients type Gmail)
  "hr", // séparateur
];

function isSignatureElement(node) {
  const id = (node.id || "").toLowerCase();
  const cls = (node.getAttribute?.("class") || "").toLowerCase();
  return id.includes("signature") || cls.includes("signature");
}

/** Retourne le premier élément délimiteur (dans l'ordre du document), ou null. */
function findKeepBoundary(doc) {
  let earliest = null;
  const consider = (node) => {
    if (
      !earliest ||
      node.compareDocumentPosition(earliest) & Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      earliest = node;
    }
  };

  for (const sel of KEEP_BOUNDARY_SELECTORS) {
    for (const node of doc.querySelectorAll(sel)) consider(node);
  }

  for (const node of doc.querySelectorAll("[id], [class]")) {
    if (isSignatureElement(node)) consider(node);
  }

  return earliest;
}

/** Remonte jusqu'à l'enfant direct du body contenant le nœud donné. */
function topLevelAncestor(node, body) {
  let n = node;
  while (n.parentNode && n.parentNode !== body) {
    n = n.parentNode;
  }
  return n;
}

// prettier-ignore
const BLOCK_TAGS = new Set([
  "P", "DIV", "LI", "UL", "OL", "TR", "TABLE",
  "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "SECTION", "ARTICLE", "PRE",
]);

/** Convertit un nœud DOM en texte en préservant les sauts de ligne des blocs. */
function domToText(root) {
  let out = "";
  const walk = (node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.nodeValue;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.tagName === "STYLE" || child.tagName === "SCRIPT") continue;
        if (child.tagName === "BR") {
          out += "\n";
          continue;
        }
        const isBlock = BLOCK_TAGS.has(child.tagName);
        if (isBlock) out += "\n";
        walk(child);
        if (isBlock) out += "\n";
      }
    }
  };
  walk(root);
  return out;
}

function normalizeText(text) {
  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Retire le nœud délimiteur et tout ce qui le suit dans l'ordre du document,
 * en conservant le contenu qui le précède à chaque niveau d'imbrication.
 * (Supprimer simplement l'ancêtre de premier niveau effacerait aussi le corps
 * lorsque tout le message est enveloppé dans un seul <div>.)
 */
function removeFromBoundary(body, boundary) {
  let node = boundary;
  while (node && node !== body) {
    let sibling = node.nextSibling;
    while (sibling) {
      const next = sibling.nextSibling;
      node.parentNode.removeChild(sibling);
      sibling = next;
    }
    node = node.parentNode;
  }
  if (boundary.parentNode) boundary.parentNode.removeChild(boundary);
}

/**
 * Extrait le corps principal d'un email HTML sous forme de texte : retire la
 * signature et le bloc de citation/réponse (et tout ce qui suit) en réutilisant
 * la même détection de délimiteur qu'en composition. Si le découpage ne laisse
 * rien, on renvoie le texte complet (repli de sécurité).
 */
function stripSignatureToText(html) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(html || "", "text/html");
  } catch {
    return html || "";
  }

  const body = doc.body;
  const fullText = normalizeText(domToText(body));

  const boundary = findKeepBoundary(doc);
  if (boundary) removeFromBoundary(body, boundary);

  const stripped = normalizeText(domToText(body));
  return stripped || fullText;
}

/**
 * Construit le nouveau corps du brouillon
 */
function buildComposeBody(currentHtml, generatedHtml) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(currentHtml || "", "text/html");
  } catch {
    return generatedHtml;
  }

  const body = doc.body;
  const boundary = findKeepBoundary(doc);
  if (!boundary) return generatedHtml;

  const anchor = topLevelAncestor(boundary, body);

  // Supprime le texte précédemment rédigé/généré, situé avant la zone conservée.
  while (body.firstChild && body.firstChild !== anchor) {
    body.removeChild(body.firstChild);
  }

  // Insère le texte généré au-dessus de la zone conservée.
  const holder = doc.createElement("div");
  holder.innerHTML = `${generatedHtml}<br>`;
  while (holder.firstChild) {
    body.insertBefore(holder.firstChild, anchor);
  }

  return body.innerHTML;
}

/** Tente d'insérer du HTML dans Outlook. */
export function insertIntoOutlook(htmlContent) {
  return new Promise((resolve, reject) => {
    const item = window.Office?.context?.mailbox?.item;
    if (!item) {
      console.log("Insertion simulée hors Outlook : ", htmlContent);
      return resolve("inserted");
    }

    if (isComposeMode() && item.body?.setAsync) {
      item.body.getAsync(Office.CoercionType.Html, (getRes) => {
        const current =
          getRes.status === Office.AsyncResultStatus.Succeeded ? getRes.value : "";
        const newBody = buildComposeBody(current, htmlContent);

        item.body.setAsync(
          newBody,
          { coercionType: Office.CoercionType.Html },
          (setRes) => {
            if (setRes.status === Office.AsyncResultStatus.Failed) reject(new Error("insert_failed"));
            else resolve("inserted");
          }
        );
      });
      return;
    }

    // Mode lecture : ouvre un formulaire de réponse pré-rempli.
    try {
      if (item.displayReplyAllForm) {
        item.displayReplyAllForm({ htmlBody: htmlContent });
        resolve("opened");
        return;
      }
    } catch {
      // Fallback
    }

    try {
      if (item.body?.setSelectedDataAsync) {
        item.body.setSelectedDataAsync(
          htmlContent,
          { coercionType: Office.CoercionType.Html },
          (res) => {
            if (res.status === Office.AsyncResultStatus.Failed) reject(new Error("insert_failed"));
            else resolve("inserted");
          }
        );
        return;
      }
    } catch {
      // Fallback
    }

    reject(new Error("impossible"));
  });
}
